import { Box, Paper, Skeleton } from '@mui/material';
import { colors } from '@src/styles';
import { card, skeletonSX } from '../utils/format';
import { LogsTableSkeleton } from './LogsTableSkeleton';

const BAR_HEIGHTS = [38, 62, 46, 80, 55, 70, 42];
const ROW_WIDTHS = ['72%', '58%', '66%', '50%', '62%', '54%', '68%', '46%'];

type RunsSkeletonProps = {
  kpiCount: number;
  side: 'leaderboard' | 'latency';
  withLogs?: boolean;
};

const KpiSkeleton = () => (
  <Paper elevation={0} sx={{ ...card, minHeight: 112 }}>
    <Skeleton variant="text" width={90} height={14} sx={skeletonSX} />
    <Skeleton variant="text" width="45%" height={44} sx={{ ...skeletonSX, mt: 0.5 }} />
    <Skeleton variant="text" width={70} height={14} sx={skeletonSX} />
  </Paper>
);

const ChartSkeleton = () => (
  <Paper elevation={0} sx={{ ...card, minWidth: 0 }}>
    <Skeleton variant="text" width={140} height={22} sx={skeletonSX} />
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 220, mt: 2, paddingX: 1 }}>
      {BAR_HEIGHTS.map((height) => (
        <Skeleton
          key={height}
          variant="rounded"
          sx={{ ...skeletonSX, flex: 1, height: `${height}%`, borderRadius: '6px 6px 2px 2px' }}
        />
      ))}
    </Box>
    <Box sx={{ height: '1px', backgroundColor: colors.brand.line, mt: 0.5 }} />
  </Paper>
);

const LeaderboardSkeleton = () => (
  <Paper elevation={0} sx={{ ...card, minWidth: 0 }}>
    <Skeleton variant="text" width={190} height={22} sx={{ ...skeletonSX, mb: 1 }} />
    {ROW_WIDTHS.map((width) => (
      <Box key={width} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, paddingY: 1 }}>
        <Skeleton variant="text" width={18} height={16} sx={skeletonSX} />
        <Skeleton variant="text" width={width} height={18} sx={{ ...skeletonSX, flex: 'none', maxWidth: '45%' }} />
        <Box sx={{ flex: 1 }} />
        <Skeleton variant="rounded" width={96} height={6} sx={{ ...skeletonSX, borderRadius: 3 }} />
        <Skeleton variant="text" width={44} height={16} sx={skeletonSX} />
      </Box>
    ))}
  </Paper>
);

const LatencySkeleton = () => (
  <Paper elevation={0} sx={{ ...card, minWidth: 0 }}>
    <Skeleton variant="text" width={170} height={22} sx={skeletonSX} />
    <Skeleton variant="text" width="70%" height={16} sx={{ ...skeletonSX, mb: 2 }} />
    {[78, 52, 64, 90].map((width) => (
      <Box key={width} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.25 }}>
        <Skeleton variant="text" width={30} height={16} sx={skeletonSX} />
        <Skeleton variant="rounded" height={22} sx={{ ...skeletonSX, width: `${width}%`, borderRadius: '6px' }} />
      </Box>
    ))}
  </Paper>
);

export function RunsSkeleton({ kpiCount, side, withLogs = false }: RunsSkeletonProps) {
  return (
    <Box aria-busy="true" aria-label="Carregando execuções" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
        {Array.from({ length: kpiCount }, (_, index) => (
          <KpiSkeleton key={index} />
        ))}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1.35fr 1fr',
          gap: 2,
          '@media (max-width:1050px)': { gridTemplateColumns: '1fr' },
        }}
      >
        <ChartSkeleton />
        {side === 'leaderboard' ? <LeaderboardSkeleton /> : <LatencySkeleton />}
      </Box>
      {withLogs && (
        <Paper elevation={0} sx={card}>
          <Skeleton variant="text" width={150} height={22} sx={{ ...skeletonSX, mb: 1 }} />
          <LogsTableSkeleton />
        </Paper>
      )}
    </Box>
  );
}
